import React, { useEffect, useRef, useState } from 'react'
import { Form, notification, Row, Spin } from 'antd'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import { Contact } from 'src/types'
import { initialValues } from 'src/views/Management/users/form/initial-values'
import { Modal } from 'src/components/Ant'
import { useT } from 'src/core/hooks/use-translation'
import * as UsersService from 'src/services/api/users'
import UserEditForm from 'src/views/Management/users/form/user-edit-form'

import validationSchema from './validation-schema'

interface Props {
  toggle(): void
  visible: boolean
}

export default function ({ toggle, visible }: Props) {
  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const refFormik = useRef(null)
  const { t } = useT()

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  function onSubmit(values: typeof initialValues) {
    setSaving(true)

    if (values.cost_center_id === '') {
      values.cost_center_id = null
      values.cost_center = null
    }

    if (values.reference_id === '') {
      values.reference_id = null
      values.reference = null
    }

    if (values.contract_id === '') {
      values.contract_id = null
      values.contract = null
    }

    UsersService.save({
      user: {
        ...values,
        contacts: [
          ...((values.emailContact &&
          values.emailContact.email_contacts.length &&
          values.emailContact.email_contacts[0].email
            ? [{ ...values.emailContact, contact_type: 'EMAIL' }]
            : []) as Contact[]),
          ...((values.phoneContact &&
          values.phoneContact.phone_contacts.length &&
          values.phoneContact.phone_contacts[0].phone_number
            ? [{ ...values.phoneContact, contact_type: 'PHONE' }]
            : []) as Contact[])
        ]
      }
    })
      .then(response => {
        if (response.status === 200) {
          notification.success({
            duration: 5,
            message: t('web.user.user-saved'),
            placement: 'bottomRight'
          })
          toggle()
          return
        }
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .catch(() => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setSaving(false)
      })
  }

  useEffect(() => {
    if (visible) {
      setLoading(true)
      UsersService.getById({ user_id: me.user.user_id, withInactive: true })
        .then(response => {
          if (response.status === 200) {
            const login = response.data.login.split('@')
            const { address, contacts } = response.data
            const addressFields = (address?.address_id && address) || null
            const emailContact = contacts.find(
              contactItem => contactItem.contact_type === 'EMAIL'
            )
            const phoneContact = contacts.find(
              contactItem => contactItem.contact_type === 'PHONE'
            )
            if (refFormik.current) {
              refFormik.current.resetForm({
                ...response.data,
                address: addressFields,
                emailContact,
                login_splited: login[0],
                login_sufix: `@${login[1]}`,
                phoneContact
              })
            }
            return
          }
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        })
        .catch(response => {
          //bugsnag?
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        })
        .finally(() => {
          setLoading(false)
        })
    } else if (refFormik.current) {
      refFormik.current.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <Formik
      initialValues={me.user}
      onSubmit={onSubmit}
      ref={refFormik}
      render={formikProps => (
        <Form onSubmit={formikProps.handleSubmit}>
          <Modal
            destroyOnClose
            okButtonProps={{ loading: saving }}
            okText="common.actions.save"
            onCancel={() => toggle()}
            onOk={() => formikProps.submitForm()}
            title="web.user.edit-user"
            visible={visible}
            width={800}
          >
            {loading ? (
              <Row justify="center" type="flex">
                <Spin style={{ margin: '16px 0' }} />
              </Row>
            ) : (
              <UserEditForm formikProps={formikProps} />
            )}
          </Modal>
        </Form>
      )}
      validationSchema={validationSchema}
    />
  )
}
